import type { TRecordType } from '@moneyforward/ap-frontend-components';
import type { ColumnType } from 'antd/es/table/interface';
import type { ReactNode } from 'react';

export const ColumnsPerRow = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
} as const;

export type TColumnsPerRow = (typeof ColumnsPerRow)[keyof typeof ColumnsPerRow];

/** Column count when the drawer is expanded to its widest (design ceiling). */
export const MAX_COLUMNS_PER_ROW = 6;

/**
 * Extends antd's `ColumnType` so a `Table` column definition can be reused
 * as-is for the card layout, avoiding a second set of column definitions.
 */
export type CardColumn<RecordType extends TRecordType> =
  ColumnType<RecordType> & {
    /** Unit for numeric values, pinned to the right edge of the cell (e.g. 円). */
    unit?: ReactNode;
    /** Secondary line under the value (e.g. account code, approver name). */
    caption?: (record: RecordType, index: number) => ReactNode;
    /** Tooltip shown by the help icon next to the label. */
    help?: ReactNode;
    /** Hide this column in the card layout only. */
    hideInCard?: boolean;
    /**
     * Label to use in card view instead of `title`. Needed when `title` is a
     * function (e.g. wrapped in `TableTextOverflow` for the fixed-width table
     * header) — cards need the raw label to apply their own clamp + tooltip.
     */
    cardLabel?: ReactNode;
    /**
     * Groups this column with other columns sharing the same key into a
     * side-by-side sub-grid within the card (e.g. 借方/貸方 for journal
     * entries). Columns are partitioned in column-array order — insertion
     * order decides both group order and in-group order. Omit for the
     * default single-grid card layout.
     */
    group?: string;
  };

export type CardHeader = {
  /** Left-aligned heading (e.g. 明細番号：AP6058). */
  title?: ReactNode;
  /** Right-aligned counter (e.g. 1/1,234件). */
  counter?: ReactNode;
  /** Actions to the right of the counter (Copy, More Vertical, ...). */
  actions?: ReactNode;
};
